import { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuthStore, api } from '../lib/auth';
import { useTranslation } from '../lib/i18n';
import { formatDate, cn } from '../lib/utils';
import { turkishIncludes } from '../lib/turkishSearch';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Calendar } from '../components/ui/calendar';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../components/ui/popover';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { toast } from 'sonner';
import { format, isSunday, parseISO } from 'date-fns';
import { tr, enUS, nl } from 'date-fns/locale';
import { 
  Search,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  Eye,
  Edit,
  Check,
  X,
  User,
  Users,
  Calendar as CalendarIcon,
  CalendarDays,
  FileText,
  AlertTriangle,
  RefreshCw,
  Trash2,
  List,
  Info,
  ShieldAlert,
  FileBarChart
} from 'lucide-react';
import { LeaveCalendarView } from '../components/LeaveCalendarView';

const statusConfig = {
  PENDING: { icon: Clock, color: 'bg-amber-50 text-amber-700 border-amber-200', label: 'pending' },
  APPROVED: { icon: CheckCircle, color: 'bg-emerald-50 text-emerald-700 border-emerald-200', label: 'approved' },
  REJECTED: { icon: XCircle, color: 'bg-red-50 text-red-700 border-red-200', label: 'rejected' },
  CANCELLED: { icon: XCircle, color: 'bg-slate-100 text-slate-600 border-slate-300 line-through decoration-slate-400', label: 'cancelled' }
};

const getDateLocale = (lang) => {
  switch (lang) {
    case 'tr': return tr;
    case 'nl': return nl;
    default: return enUS;
  }
};

// Request Row Component - moved outside to prevent re-render issues
const RequestRow = ({ 
  request, 
  t, 
  dateLocale,
  overlappingLeaves,
  onViewDetail, 
  onApprove, 
  onReject, 
  onEdit, 
  onDelete,
  onHardDelete,
  isSuperAdmin
}) => {
  const config = statusConfig[request.status] || statusConfig.PENDING;
  const StatusIcon = config.icon;
  
  // Filter overlapping leaves for this request's dates
  const relevantOverlaps = overlappingLeaves || [];
  
  return (
    <div className="bg-white dark:bg-slate-800 border border-slate-100 dark:border-slate-700 rounded-lg p-4 hover:shadow-sm transition-shadow">
      {/* Mobile Layout */}
      <div className="flex flex-col gap-3 sm:hidden">
        {/* Header: User + Status */}
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center flex-shrink-0">
              <User className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </div>
            <div className="min-w-0">
              <p className="font-medium text-slate-900 dark:text-slate-100 truncate">{request.user_name}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{request.leave_type_name}</p>
            </div>
          </div>
          <Badge variant="outline" className={cn("flex items-center gap-1 flex-shrink-0", config.color)}>
            <StatusIcon className="w-3 h-3" />
            <span className="hidden xs:inline">{t(`leave.${config.label}`)}</span>
          </Badge>
        </div>
        
        {/* Date & Duration Info */}
        <div className="flex flex-wrap gap-2 text-sm">
          <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 px-2 py-1 rounded">
            <CalendarIcon className="w-3.5 h-3.5" />
            <span>
              {request.selected_days && request.selected_days.length > 0 
                ? (request.selected_days.length <= 2 
                    ? request.selected_days.map(d => format(new Date(d), 'd MMM', { locale: dateLocale })).join(', ')
                    : `${format(new Date(request.selected_days[0]), 'd MMM', { locale: dateLocale })} +${request.selected_days.length - 1}`)
                : `${request.start_date} - ${request.end_date}`
              }
            </span>
          </div>
          <div className="flex items-center gap-1 text-slate-600 dark:text-slate-300 bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 px-2 py-1 rounded">
            <FileText className="w-3.5 h-3.5" />
            <span>{request.days_count || request.total_days} {t('leave.day')}</span>
          </div>
        </div>
        
        {/* Timestamp */}
        {request.created_at && (
          <div className="flex items-center gap-1 text-xs text-slate-400">
            <Clock className="w-3 h-3" />
            <span>{format(new Date(request.created_at), 'd MMM yyyy HH:mm', { locale: dateLocale })}</span>
          </div>
        )}
        
        {/* Action Buttons - Mobile */}
        <div className="flex items-center justify-end gap-1 pt-2 border-t border-slate-100 dark:border-slate-700">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2"
            onClick={() => onViewDetail(request)}
          >
            <Eye className="h-4 w-4" />
          </Button>
          
          {request.status === 'PENDING' && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                onClick={() => onApprove(request)}
              >
                <Check className="h-4 w-4" />
              </Button>
              <Button
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                onClick={() => onReject(request)}
              >
                <X className="h-4 w-4" />
              </Button>
            </>
          )}
          
          {(request.status === 'PENDING' || request.status === 'APPROVED') && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2"
              onClick={() => onEdit(request)}
            >
              <Edit className="h-4 w-4" />
            </Button>
          )}
          
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-red-600 hover:text-red-700 hover:bg-red-50"
            onClick={() => onDelete(request)}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
          
          {isSuperAdmin && (
            <Button
              variant="ghost"
              size="sm"
              className="h-8 px-2 text-red-800 hover:text-white hover:bg-red-700 border border-red-300"
              onClick={() => onHardDelete(request)}
              title={t('admin.hardDeleteTooltip')}
              data-testid={`hard-delete-btn-mobile-${request.id}`}
            >
              <ShieldAlert className="h-4 w-4" />
            </Button>
          )}
        </div>
        
        {/* Overlapping Leaves Info - Mobile */}
        {request.status === 'PENDING' && relevantOverlaps.length > 0 && (
          <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-amber-700 text-xs font-medium mb-2">
              <Users className="w-3.5 h-3.5" />
              <span>{t('admin.samePeriodsOnLeave') }</span>
            </div>
            <div className="space-y-1.5">
              {relevantOverlaps.slice(0, 2).map((overlap, idx) => (
                <div key={idx} className="text-xs text-amber-700">
                  <span className="font-medium">{overlap.user_name}</span>
                  <span className="text-amber-600"> ({overlap.leave_type_name})</span>
                </div>
              ))}
              {relevantOverlaps.length > 2 && (
                <div className="text-xs text-amber-600">+{relevantOverlaps.length - 2} {t('leave.more') }</div>
              )}
            </div>
          </div>
        )}
      </div>
      
      {/* Desktop Layout */}
      <div className="hidden sm:flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-slate-100 dark:bg-slate-700 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-slate-600 dark:text-slate-300" />
            </div>
            <div>
              <p className="font-medium text-slate-900 dark:text-slate-100">{request.user_name}</p>
              <p className="text-sm text-slate-500 dark:text-slate-400">{request.leave_type_name}</p>
            </div>
          </div>
          
          <div className="flex flex-wrap items-center gap-4 text-sm text-slate-600 dark:text-slate-300 mt-3">
            <div className="flex items-center gap-1">
              <CalendarIcon className="w-4 h-4" />
              <span>
                {request.selected_days && request.selected_days.length > 0 
                  ? (request.selected_days.length <= 3 
                      ? request.selected_days.map(d => format(new Date(d), 'd MMM', { locale: dateLocale })).join(', ')
                      : `${request.selected_days.slice(0, 3).map(d => format(new Date(d), 'd MMM', { locale: dateLocale })).join(', ')} +${request.selected_days.length - 3}`)
                  : `${request.start_date} - ${request.end_date}`
                }
              </span>
            </div>
            <div className="flex items-center gap-1">
              <FileText className="w-4 h-4" />
              <span>{request.days_count || request.total_days} {t('leave.days')}</span>
            </div>
            {request.created_at && (
              <div className="flex items-center gap-1 text-slate-400">
                <Clock className="w-4 h-4" />
                <span>{format(new Date(request.created_at), 'd MMM yyyy HH:mm', { locale: dateLocale })}</span>
              </div>
            )}
          </div>
          
          {/* Overlapping Leaves Info - Desktop */}
          {request.status === 'PENDING' && relevantOverlaps.length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-center gap-2 text-amber-700 text-sm font-medium mb-2">
                <Users className="w-4 h-4" />
                <span>{t('admin.samePeriodsOnLeave') }</span>
              </div>
              <div className="space-y-2">
                {relevantOverlaps.map((overlap, idx) => (
                  <div key={idx} className="flex items-start gap-2 text-sm">
                    <div className="w-6 h-6 bg-amber-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      <User className="w-3 h-3 text-amber-600" />
                    </div>
                    <div className="flex-1">
                      <span className="font-medium text-amber-800">{overlap.user_name}</span>
                      <span className="text-amber-600 ml-1">({overlap.leave_type_name})</span>
                      <div className="text-amber-600 text-xs mt-0.5">
                        {t('admin.overlappingDates') }: {' '}
                        {overlap.overlapping_dates.map(d => format(new Date(d), 'd MMM', { locale: dateLocale })).join(', ')}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
        
        <div className="flex flex-col items-end gap-2">
          <Badge variant="outline" className={cn("flex items-center gap-1", config.color)}>
            <StatusIcon className="w-3 h-3" />
            {t(`leave.${config.label}`)}
          </Badge>
          
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => onViewDetail(request)}
            >
              <Eye className="h-4 w-4" />
            </Button>
            
            {request.status === 'PENDING' && (
              <>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                  onClick={() => onApprove(request)}
                >
                  <Check className="h-4 w-4" />
                </Button>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => onReject(request)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </>
            )}
            
            {(request.status === 'PENDING' || request.status === 'APPROVED') && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => onEdit(request)}
              >
                <Edit className="h-4 w-4" />
              </Button>
            )}
            
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
              onClick={() => onDelete(request)}
              title={t('common.delete')}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            
            {isSuperAdmin && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-800 hover:text-white hover:bg-red-700 border border-red-300"
                onClick={() => onHardDelete(request)}
                title={t('admin.hardDeleteTooltip')}
                data-testid={`hard-delete-btn-${request.id}`}
              >
                <ShieldAlert className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export const AdminLeaveRequests = () => {
  const { t, language } = useTranslation();
  const { user } = useAuthStore();
  const dateLocale = getDateLocale(language);
  const [searchParams, setSearchParams] = useSearchParams();
  const nowYear = new Date().getFullYear();

  const [viewMode, setViewMode] = useState(() => searchParams.get('view') || 'list'); // 'list' or 'calendar'
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(() => parseInt(searchParams.get('year'), 10) || nowYear);
  const [activeTab, setActiveTab] = useState(() => searchParams.get('tab') || 'PENDING');
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('q') || '');
  const [firms, setFirms] = useState([]);
  const [deleteDialog, setDeleteDialog] = useState({ open: false, request: null, step: 1 });
  const [hardDeleteDialog, setHardDeleteDialog] = useState({ open: false, request: null, confirmText: '' });
  const [selectedFirm, setSelectedFirm] = useState(() => searchParams.get('firm') || 'all');
  const [processing, setProcessing] = useState(false);

  // Pagination — URL-backed
  const [currentPage, setCurrentPage] = useState(() => parseInt(searchParams.get('page'), 10) || 1);
  const [pageSize, setPageSize] = useState(() => parseInt(searchParams.get('size'), 10) || 20);
  const [totalItems, setTotalItems] = useState(0);

  // Filters
  const [leaveTypeFilter, setLeaveTypeFilter] = useState(() => searchParams.get('type') || 'all');
  const [availableLeaveTypes, setAvailableLeaveTypes] = useState([]);

  // Sync filters/pagination → URL so back-navigation restores the list view.
  useEffect(() => {
    const next = new URLSearchParams();
    if (viewMode !== 'list') next.set('view', viewMode);
    if (selectedYear !== nowYear) next.set('year', String(selectedYear));
    if (activeTab !== 'PENDING') next.set('tab', activeTab);
    if (searchQuery) next.set('q', searchQuery);
    if (selectedFirm !== 'all') next.set('firm', selectedFirm);
    if (leaveTypeFilter !== 'all') next.set('type', leaveTypeFilter);
    if (currentPage !== 1) next.set('page', String(currentPage));
    if (pageSize !== 20) next.set('size', String(pageSize));
    setSearchParams(next, { replace: true });
  }, [viewMode, selectedYear, activeTab, searchQuery, selectedFirm, leaveTypeFilter, currentPage, pageSize, nowYear, setSearchParams]);
  
  // Dialog states
  const [detailDialog, setDetailDialog] = useState({ open: false, request: null });
  const [approveDialog, setApproveDialog] = useState({ open: false, request: null });
  const [rejectDialog, setRejectDialog] = useState({ open: false, request: null });
  const [editDialog, setEditDialog] = useState({ open: false, request: null });

  // Şub 2026 — Otomatik iptal raporu (audit log kaynaklı haftalık özet)
  const [autoCancelReportOpen, setAutoCancelReportOpen] = useState(false);
  const [autoCancelReport, setAutoCancelReport] = useState({ loading: false, data: null });
  const [autoCancelRange, setAutoCancelRange] = useState({ from: '', to: '' });
  
  // Form states
  const [rejectReason, setRejectReason] = useState('');
  const [adminNote, setAdminNote] = useState('');
  const [notifyOptions, setNotifyOptions] = useState({ sms: false, email: true, whatsapp: false });
  
  // Edit form state
  const [editForm, setEditForm] = useState({
    selected_days: [],
    description: '',
    admin_note: ''
  });
  const [editCalendarMonth, setEditCalendarMonth] = useState(new Date());
  
  // Overlapping leaves cache - keyed by request ID
  const [overlappingCache, setOverlappingCache] = useState({});

  // Admin Create Leave Request Dialog
  const [createDialog, setCreateDialog] = useState({ open: false });
  const [createForm, setCreateForm] = useState({
    user_id: '',
    leave_type_code: '',
    start_date: null,
    end_date: null,
    selected_days: [],
    description: '',
    auto_approve: true
  });
  const [allUsers, setAllUsers] = useState([]);
  const [allLeaveTypes, setAllLeaveTypes] = useState([]);
  const [createCalendarMonth, setCreateCalendarMonth] = useState(new Date());
  const [userSearch, setUserSearch] = useState('');

  const isSuperAdmin = user?.role === 'SUPER_ADMIN';
  const isAnyAdmin = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN' || user?.role === 'FIRM_ADMIN';
  const canFilterFirm = user?.role === 'SUPER_ADMIN' || user?.role === 'FULL_ADMIN';

  useEffect(() => {
    if (canFilterFirm) {
      fetchFirms();
    }
    fetchAvailableLeaveTypes();
    fetchRequests();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, activeTab, selectedFirm, currentPage, pageSize]);
  
  // Reset page when filters change. Guarded by a ref so URL-initialized
  // currentPage (e.g. ?page=3) isn't clobbered back to 1 on first render.
  const leaveFiltersMountedRef = useRef(false);
  useEffect(() => {
    if (!leaveFiltersMountedRef.current) {
      leaveFiltersMountedRef.current = true;
      return;
    }
    setCurrentPage(1);
  }, [selectedYear, activeTab, selectedFirm, leaveTypeFilter]);

  const fetchAvailableLeaveTypes = async () => {
    try {
      const response = await api.get('/leave-types/all-active');
      if (response.data.success) {
        setAvailableLeaveTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch leave types', error);
    }
  };

  const fetchFirms = async () => {
    try {
      const response = await api.get('/admin/firms');
      if (response.data.success) {
        setFirms(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch firms', error);
    }
  };

  // Şub 2026 — Otomatik iptal edilen izin raporu
  const fetchAutoCancelReport = async () => {
    setAutoCancelReport({ loading: true, data: null });
    try {
      const params = new URLSearchParams();
      if (autoCancelRange.from) params.set('from_date', autoCancelRange.from);
      if (autoCancelRange.to) params.set('to_date', autoCancelRange.to);
      const qs = params.toString();
      const response = await api.get(`/admin/leave-requests/reports/auto-cancelled${qs ? '?' + qs : ''}`);
      if (response.data?.success) {
        setAutoCancelReport({ loading: false, data: response.data.data });
      } else {
        setAutoCancelReport({ loading: false, data: null });
      }
    } catch (error) {
      console.error('auto-cancel report fetch failed', error);
      setAutoCancelReport({ loading: false, data: null });
    }
  };

  const fetchAllUsers = async () => {
    try {
      // Use /admin/users - it already filters by firm for non-super-admins
      const response = await api.get('/admin/users?status=ACTIVE');
      if (response.data.success) {
        const users = response.data.data || [];
        // Filter: show all except SUPER_ADMIN (includes EMPLOYEE, FIRM_ADMIN, FULL_ADMIN)
        setAllUsers(users.filter(u => u.role !== 'SUPER_ADMIN'));
      }
    } catch (error) {
      console.error('Failed to fetch users', error);
    }
  };

  const fetchAllLeaveTypes = async () => {
    try {
      const response = await api.get('/leave-types/all-active');
      if (response.data.success) {
        setAllLeaveTypes(response.data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch leave types', error);
    }
  };

  const openCreateDialog = () => {
    fetchAllUsers();
    fetchAllLeaveTypes();
    setCreateForm({
      user_id: '',
      leave_type_code: '',
      start_date: null,
      end_date: null,
      selected_days: [],
      description: '',
      auto_approve: true
    });
    setUserSearch('');
    setCreateCalendarMonth(new Date());
    setCreateDialog({ open: true });
  };

  const handleCreateLeaveRequest = async () => {
    if (!createForm.user_id || !createForm.leave_type_code || createForm.selected_days.length === 0) {
      toast.error(t('leaveRequestsAdmin.fillRequired') );
      return;
    }

    setProcessing(true);
    try {
      const sortedDays = [...createForm.selected_days].sort();
      const payload = {
        user_id: createForm.user_id,
        leave_type_code: createForm.leave_type_code,
        year: parseISO(sortedDays[0]).getFullYear(),
        start_date: sortedDays[0],
        end_date: sortedDays[sortedDays.length - 1],
        selected_days: sortedDays,
        description: createForm.description,
        auto_approve: createForm.auto_approve
      };

      const response = await api.post('/admin/leave-requests/create-for-user', payload);
      if (response.data.success) {
        toast.success(
          createForm.auto_approve 
            ? t('leaveRequestsAdmin.createdAndApproved') 
            : t('leaveRequestsAdmin.created') 
        );
        setCreateDialog({ open: false });
        fetchRequests();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  };

  // Get selected leave type info
  const getSelectedLeaveTypeInfo = () => {
    if (!createForm.leave_type_code) return null;
    return allLeaveTypes.find(lt => lt.code === createForm.leave_type_code);
  };

  // Toggle day selection in create form
  const toggleCreateDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    const selectedLeaveType = getSelectedLeaveTypeInfo();
    const maxDays = selectedLeaveType?.default_limit_days;
    
    setCreateForm(prev => {
      const isSelected = prev.selected_days.includes(dateStr);
      let newSelectedDays;
      
      if (isSelected) {
        // Remove day
        newSelectedDays = prev.selected_days.filter(d => d !== dateStr);
      } else {
        // Add day - but check max_days limit
        if (maxDays && prev.selected_days.length >= maxDays) {
          toast.error(t('leave.maxDaysReached')?.replace('{days}', maxDays) || `Maksimum ${maxDays} gün seçebilirsiniz`);
          return prev;
        }
        newSelectedDays = [...prev.selected_days, dateStr].sort();
      }
      // Auto-calculate start_date and end_date from selected_days
      const startDate = newSelectedDays.length > 0 ? parseISO(newSelectedDays[0]) : null;
      const endDate = newSelectedDays.length > 0 ? parseISO(newSelectedDays[newSelectedDays.length - 1]) : null;
      return { ...prev, selected_days: newSelectedDays, start_date: startDate, end_date: endDate };
    });
  };

  // Check if a date is selected in create form
  const isCreateDaySelected = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return createForm.selected_days.includes(dateStr);
  };

  const filteredUsersForCreate = allUsers.filter(u =>
    !userSearch ||
    turkishIncludes(u.full_name, userSearch) ||
    turkishIncludes(u.email, userSearch)
  );

  const fetchRequests = async () => {
    setLoading(true);
    try {
      let url = `/admin/leave-requests?year=${selectedYear}&page=${currentPage}&limit=${pageSize}`;
      if (activeTab !== 'all') {
        url += `&status=${activeTab}`;
      }
      if (canFilterFirm && selectedFirm !== 'all') {
        url += `&firm_id=${selectedFirm}`;
      }
      if (searchQuery) {
        url += `&q=${encodeURIComponent(searchQuery)}`;
      }
      
      const response = await api.get(url);
      if (response.data.success) {
        const data = response.data.data;
        let fetchedRequests = data.requests || data || [];
        const total = data.total || fetchedRequests.length;
        
        // Client-side leave type filter
        if (leaveTypeFilter && leaveTypeFilter !== 'all') {
          fetchedRequests = fetchedRequests.filter(r => r.leave_type_code === leaveTypeFilter);
        }
        
        setRequests(fetchedRequests);
        setTotalItems(total);
        
        // Fetch overlapping leaves for PENDING requests
        const pendingRequests = fetchedRequests.filter(r => r.status === 'PENDING');
        if (pendingRequests.length > 0) {
          fetchOverlappingForRequests(pendingRequests);
        }
      }
    } catch (error) {
      toast.error(t('common.error'));
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch overlapping leaves for pending requests
  const fetchOverlappingForRequests = async (pendingRequests) => {
    const newCache = { ...overlappingCache };
    
    for (const request of pendingRequests) {
      // Skip if already cached
      if (newCache[request.id]) continue;
      
      const dates = request.selected_days || [];
      if (dates.length === 0) continue;
      
      try {
        const response = await api.post('/admin/leave-requests/overlapping', {
          dates: dates,
          firm_id: request.firm_id,
          exclude_user_id: request.user_id
        });
        
        if (response.data.success) {
          newCache[request.id] = response.data.data || [];
        }
      } catch (error) {
        console.error('Failed to fetch overlapping leaves for request', request.id, error);
        newCache[request.id] = [];
      }
    }
    
    setOverlappingCache(newCache);
  };

  const handleSearch = () => {
    fetchRequests();
  };

  const openDetail = async (request) => {
    try {
      const response = await api.get(`/admin/leave-requests/${request.id}`);
      if (response.data.success) {
        setDetailDialog({ open: true, request: response.data.data });
      }
    } catch (error) {
      toast.error(t('common.error'));
    }
  };

  // Şub 27, 2026 — Warning mesajlarını yerelleştir + SUPER_ADMIN override durumunu göster
  const translateWarning = (w) => {
    const code = w?.code;
    if (code === 'insufficient_balance') {
      return t('admin.warn.insufficientBalance', { requested: w.requested, available: w.available })
        || `Yetersiz bakiye: ${w.requested} istendi, ${w.available} mevcut`;
    }
    if (code === 'days_fully_booked') {
      const dates = (w.blocked_days || []).slice(0, 5).join(', ');
      return t('admin.warn.daysFullyBooked', { dates })
        || `Bu günler tam dolu: ${dates}`;
    }
    if (code === 'locked_dates') {
      const dates = (w.locked_dates || []).slice(0, 5).map(d => `${d.date}${d.reason ? ' (' + d.reason + ')' : ''}`).join(', ');
      return t('admin.warn.lockedDates', { dates }) || `Kilitli tarihler: ${dates}`;
    }
    if (code === 'date_overlap') {
      return t('admin.warn.dateOverlap') || 'Tarihler başka bir onaylı izinle çakışıyor';
    }
    if (code === 'no_balance') {
      return t('admin.warn.noBalance') || 'İzin bakiyesi bulunamadı';
    }
    return w?.message || '';
  };

  const handleApprove = async () => {
    setProcessing(true);
    try {
      const response = await api.put(`/admin/leave-requests/${approveDialog.request.id}/approve`, {
        admin_note: adminNote || null,
        notify: notifyOptions
      });
      if (response.data.success) {
        const warnings = response.data.data?.warnings || [];
        const overridden = response.data.data?.override;
        if (warnings.length > 0 && overridden) {
          const lines = warnings.map(translateWarning).filter(Boolean);
          toast.warning(
            `${t('admin.leaveApprovedWithWarnings') || 'İzin onaylandı (uyarılarla)'}: ${lines.join(' · ')}`,
            { duration: 8000 }
          );
        } else if (warnings.length > 0) {
          toast.warning(warnings.map(translateWarning).join(' · '), { duration: 8000 });
        } else {
          toast.success(t('admin.leaveApproved'));
        }
        setApproveDialog({ open: false, request: null });
        setAdminNote('');
        fetchRequests();
      }
    } catch (error) {
      // Backend'ten gelen İngilizce mesajı bilinen pattern'lerle Türkçe/lokal karşılığa çevir
      let detail = error.response?.data?.detail || t('common.error');
      if (typeof detail === 'string') {
        if (/fully booked/i.test(detail)) {
          const m = detail.match(/:\s*([^\s].+)$/);
          detail = t('admin.warn.daysFullyBooked', { dates: m ? m[1] : '' })
            || `Bu günler tam dolu: ${m ? m[1] : ''}`;
        } else if (/Insufficient leave balance/i.test(detail)) {
          const req = detail.match(/Requested:\s*([\d.]+)/)?.[1];
          const avail = detail.match(/Available:\s*(-?[\d.]+)/)?.[1];
          detail = t('admin.warn.insufficientBalance', { requested: req, available: avail })
            || `Yetersiz bakiye: ${req} istendi, ${avail} mevcut`;
        } else if (/overlap/i.test(detail)) {
          detail = t('admin.warn.dateOverlap') || 'Tarihler başka bir onaylı izinle çakışıyor';
        } else if (/Locked date/i.test(detail)) {
          detail = t('admin.warn.lockedDates', { dates: detail.split(':')[1]?.trim() || '' }) || detail;
        } else if (/Balance not found/i.test(detail)) {
          detail = t('admin.warn.noBalance') || 'İzin bakiyesi bulunamadı';
        }
      }
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (rejectReason.length < 3) {
      toast.error(t('admin.rejectReasonRequired'));
      return;
    }
    setProcessing(true);
    try {
      const response = await api.put(`/admin/leave-requests/${rejectDialog.request.id}/reject`, {
        reason: rejectReason,
        notify: notifyOptions
      });
      if (response.data.success) {
        toast.success(t('admin.leaveRejected'));
        setRejectDialog({ open: false, request: null });
        setRejectReason('');
        fetchRequests();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  };

  // Check if request has dates in the past
  const isOldDateRequest = (request) => {
    if (!request) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const dates = request.selected_days || [];
    if (dates.length > 0) {
      return dates.some(d => new Date(d) < today);
    }
    if (request.start_date) {
      return new Date(request.start_date) < today;
    }
    return false;
  };

  const handleDeleteFirstStep = () => {
    if (!deleteDialog.request) return;
    // If old date request, show second warning
    if (isOldDateRequest(deleteDialog.request)) {
      setDeleteDialog(prev => ({ ...prev, step: 2 }));
    } else {
      // Not old, delete directly
      handleDeleteRequest();
    }
  };

  const handleDeleteRequest = async () => {
    if (!deleteDialog.request) return;
    
    try {
      const response = await api.delete(`/admin/leave-requests/${deleteDialog.request.id}`);
      if (response.data.success) {
        toast.success(t('admin.leaveRequestDeleted'));
        setDeleteDialog({ open: false, request: null, step: 1 });
        fetchRequests();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    }
  };

  const handleHardDeleteRequest = async () => {
    if (!hardDeleteDialog.request) return;
    if (hardDeleteDialog.confirmText !== t('admin.hardDeleteConfirmWord')) {
      toast.error(t('admin.hardDeleteConfirmPrompt'));
      return;
    }
    try {
      const response = await api.delete(`/admin/leave-requests/${hardDeleteDialog.request.id}?force=true`);
      if (response.data.success) {
        toast.success(t('admin.hardDeleteSuccess'));
        setHardDeleteDialog({ open: false, request: null, confirmText: '' });
        fetchRequests();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    }
  };

  const handleEdit = async () => {
    if (editForm.selected_days.length === 0) {
      toast.error(t('leave.noDaysSelected') );
      return;
    }
    
    setProcessing(true);
    try {
      const sortedDays = [...editForm.selected_days].sort();
      const payload = {
        selected_days: sortedDays,
        start_date: sortedDays[0],
        end_date: sortedDays[sortedDays.length - 1],
        days_count: sortedDays.length,
        description: editForm.description,
        admin_note: editForm.admin_note
      };
      
      const response = await api.put(`/admin/leave-requests/${editDialog.request.id}`, payload);
      if (response.data.success) {
        toast.success(t('admin.leaveUpdated'));
        setEditDialog({ open: false, request: null });
        fetchRequests();
      }
    } catch (error) {
      const detail = error.response?.data?.detail || t('common.error');
      toast.error(detail);
    } finally {
      setProcessing(false);
    }
  };

  const openEditDialog = (request) => {
    // Get selected days from request, or generate from date range
    let selectedDays = request.selected_days || [];
    if (selectedDays.length === 0 && request.start_date && request.end_date) {
      // Generate dates from range (excluding Sundays)
      const start = parseISO(request.start_date);
      const end = parseISO(request.end_date);
      const days = [];
      let current = new Date(start);
      while (current <= end) {
        if (!isSunday(current)) {
          days.push(format(current, 'yyyy-MM-dd'));
        }
        current.setDate(current.getDate() + 1);
      }
      selectedDays = days;
    }
    
    setEditForm({
      selected_days: selectedDays,
      description: request.description || '',
      admin_note: request.admin_note || ''
    });
    
    // Set calendar to first selected day's month
    if (selectedDays.length > 0) {
      setEditCalendarMonth(parseISO(selectedDays[0]));
    }
    
    setEditDialog({ open: true, request });
  };

  // Toggle day selection in edit form
  const toggleEditDay = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    setEditForm(prev => {
      const isSelected = prev.selected_days.includes(dateStr);
      if (isSelected) {
        return { ...prev, selected_days: prev.selected_days.filter(d => d !== dateStr) };
      } else {
        return { ...prev, selected_days: [...prev.selected_days, dateStr].sort() };
      }
    });
  };

  // Check if a date is selected in edit form
  const isEditDaySelected = (date) => {
    const dateStr = format(date, 'yyyy-MM-dd');
    return editForm.selected_days.includes(dateStr);
  };

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 
            className="text-2xl font-bold text-slate-900 dark:text-slate-100"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            {t('admin.leaveRequestManagement')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 text-sm mt-1">{selectedYear}</p>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Add Leave for User Button */}
          <Button 
            onClick={openCreateDialog}
            className="bg-emerald-600 hover:bg-emerald-700"
          >
            <CalendarIcon className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">{t('leaveRequestsAdmin.addForUser') }</span>
            <span className="sm:hidden">+</span>
          </Button>
          
          {/* View Mode Toggle */}
          <div className="flex items-center bg-slate-100 dark:bg-slate-700 rounded-lg p-1">
            <Button
              variant={viewMode === 'list' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('list')}
              className={cn(
                "flex items-center gap-2 px-3",
                viewMode === 'list' && "bg-white dark:bg-slate-800 shadow-sm"
              )}
            >
              <List className="h-4 w-4" />
              <span className="hidden sm:inline">{t('admin.listView') }</span>
            </Button>
            <Button
              variant={viewMode === 'calendar' ? 'secondary' : 'ghost'}
              size="sm"
              onClick={() => setViewMode('calendar')}
              className={cn(
                "flex items-center gap-2 px-3",
                viewMode === 'calendar' && "bg-white dark:bg-slate-800 shadow-sm"
              )}
            >
              <CalendarDays className="h-4 w-4" />
              <span className="hidden sm:inline">{t('admin.calendarView') }</span>
            </Button>
          </div>
          
          {viewMode === 'list' && (
            <>
              <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map(year => (
                    <SelectItem key={year} value={String(year)}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              
              {canFilterFirm && (
                <Select value={selectedFirm} onValueChange={setSelectedFirm}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder={t('admin.selectFirm')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t('admin.allRequests')}</SelectItem>
                    {firms.map(firm => (
                      <SelectItem key={firm.id} value={firm.id}>{firm.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              
              <Button
                variant="outline"
                size="icon"
                onClick={fetchRequests}
                disabled={loading}
              >
                <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { setAutoCancelReportOpen(true); fetchAutoCancelReport(); }}
                data-testid="auto-cancel-report-btn"
                className="gap-1"
              >
                <FileBarChart className="h-4 w-4" />
                Oto. İptal Raporu
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Calendar View */}
      {viewMode === 'calendar' && (
        <LeaveCalendarView />
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {/* Search & Filters */}
          <div className="flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder={t('common.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && fetchRequests()}
                className="pl-10"
                data-testid="leave-search"
              />
            </div>
            <Select value={leaveTypeFilter} onValueChange={(v) => { setLeaveTypeFilter(v); setCurrentPage(1); fetchRequests(); }}>
              <SelectTrigger className="w-full sm:w-[200px]" data-testid="leave-type-filter">
                <SelectValue placeholder="İzin Tipi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tüm İzin Tipleri</SelectItem>
                {availableLeaveTypes.map(lt => (
                  <SelectItem key={lt.code} value={lt.code}>{lt.name_i18n?.[language] || lt.name_i18n?.tr || lt.code}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button onClick={() => { setCurrentPage(1); fetchRequests(); }} data-testid="leave-search-btn">{t('common.search')}</Button>
          </div>

          {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
          <TabsList className="w-full lg:w-auto">
            <TabsTrigger value="all" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm">
              {t('common.all')}
            </TabsTrigger>
            <TabsTrigger value="PENDING" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm">
              {t('admin.pendingTab')}
            </TabsTrigger>
            <TabsTrigger value="APPROVED" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm">
              {t('leave.approved')}
            </TabsTrigger>
            <TabsTrigger value="REJECTED" className="flex-shrink-0 px-3 sm:px-2 text-xs sm:text-sm">
              {t('leave.rejected')}
            </TabsTrigger>
          </TabsList>
        </div>

        <TabsContent value={activeTab} className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : requests.length === 0 ? (
            <div className="text-center py-12 text-slate-500 dark:text-slate-400">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-30" />
              <p>{t('common.noData')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {requests.map(request => (
                <RequestRow 
                  key={request.id} 
                  request={request}
                  t={t}
                  dateLocale={dateLocale}
                  overlappingLeaves={overlappingCache[request.id] || []}
                  onViewDetail={openDetail}
                  onApprove={(request) => setApproveDialog({ open: true, request })}
                  onReject={(request) => setRejectDialog({ open: true, request })}
                  onEdit={openEditDialog}
                  onDelete={(request) => setDeleteDialog({ open: true, request, step: 1 })}
                  onHardDelete={(request) => setHardDeleteDialog({ open: true, request, confirmText: '' })}
                  isSuperAdmin={isSuperAdmin}
                />
              ))}
            </div>
          )}
          
          {/* Pagination */}
          {!loading && totalItems > 0 && (
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-4 mt-4 border-t">
              <div className="flex items-center gap-2 text-sm text-slate-500">
                <span>Göster:</span>
                <Select value={String(pageSize)} onValueChange={(v) => { setPageSize(Number(v)); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[70px] h-8" data-testid="leave-page-size">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span>
                  {Math.min((currentPage - 1) * pageSize + 1, totalItems)} - {Math.min(currentPage * pageSize, totalItems)} / {totalItems}
                </span>
              </div>
              <div className="flex items-center gap-1">
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(1)} data-testid="leave-page-first">
                  İlk
                </Button>
                <Button variant="outline" size="sm" disabled={currentPage === 1} onClick={() => setCurrentPage(p => p - 1)} data-testid="leave-page-prev">
                  Önceki
                </Button>
                <span className="px-3 text-sm font-medium">
                  {currentPage} / {Math.max(1, Math.ceil(totalItems / pageSize))}
                </span>
                <Button variant="outline" size="sm" disabled={currentPage >= Math.ceil(totalItems / pageSize)} onClick={() => setCurrentPage(p => p + 1)} data-testid="leave-page-next">
                  Sonraki
                </Button>
                <Button variant="outline" size="sm" disabled={currentPage >= Math.ceil(totalItems / pageSize)} onClick={() => setCurrentPage(Math.ceil(totalItems / pageSize))} data-testid="leave-page-last">
                  Son
                </Button>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
        </>
      )}

      {/* Detail Dialog */}
      <Dialog open={detailDialog.open} onOpenChange={(open) => setDetailDialog({ open, request: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{t('leave.viewDetails')}</DialogTitle>
          </DialogHeader>
          {detailDialog.request && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('admin.requester')}</Label>
                  <p className="font-medium">{detailDialog.request.user_name}</p>
                  <p className="text-sm text-slate-500 dark:text-slate-400">{detailDialog.request.user_email}</p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('admin.leaveType')}</Label>
                  <p className="font-medium">{detailDialog.request.leave_type_name}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-slate-500 dark:text-slate-400">{t('leave.selectedDates') }</Label>
                  {detailDialog.request.selected_days && detailDialog.request.selected_days.length > 0 ? (
                    <div className="flex flex-wrap gap-1 mt-1">
                      {detailDialog.request.selected_days.map((day, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {format(new Date(day), 'd MMM yyyy', { locale: dateLocale })}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="font-medium">{detailDialog.request.start_date} - {detailDialog.request.end_date}</p>
                  )}
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('admin.daysCount')}</Label>
                  <p className="font-medium">{detailDialog.request.days_count || detailDialog.request.total_days} {t('leave.days')}</p>
                </div>
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('leave.requestedAt') }</Label>
                  <p className="font-medium">
                    {detailDialog.request.created_at 
                      ? format(new Date(detailDialog.request.created_at), 'd MMMM yyyy, HH:mm', { locale: dateLocale })
                      : '-'
                    }
                  </p>
                </div>
              </div>
              
              {detailDialog.request.description && (
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('leave.description')}</Label>
                  <p className="mt-1 p-3 bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 rounded-lg text-sm">{detailDialog.request.description}</p>
                </div>
              )}
              
              {detailDialog.request.admin_note && (
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('admin.adminNote')}</Label>
                  <p className="mt-1 p-3 bg-amber-50 rounded-lg text-sm whitespace-pre-wrap">{detailDialog.request.admin_note}</p>
                </div>
              )}

              {detailDialog.request.status === 'CANCELLED' && (detailDialog.request.cancelled_by_name || detailDialog.request.cancelled_at) && (
                <div data-testid="leave-cancelled-info">
                  <Label className="text-slate-500 dark:text-slate-400">İptal Bilgisi</Label>
                  <div className="mt-1 p-3 bg-slate-50 dark:bg-slate-800 rounded-lg text-sm space-y-1">
                    {detailDialog.request.cancelled_by_name && (
                      <div><span className="text-slate-500">İptal eden:</span> <span className="font-medium">{detailDialog.request.cancelled_by_name}</span></div>
                    )}
                    {detailDialog.request.cancelled_at && (
                      <div><span className="text-slate-500">Tarih:</span> <span className="font-mono">{new Date(detailDialog.request.cancelled_at).toLocaleString('tr-TR')}</span></div>
                    )}
                  </div>
                </div>
              )}
              
              {/* Balance Info */}
              {detailDialog.request.user_balances && (
                <div>
                  <Label className="text-slate-500 dark:text-slate-400">{t('admin.balanceInfo')}</Label>
                  <div className="mt-2 p-3 bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 rounded-lg">
                    {detailDialog.request.user_balances.map(balance => (
                      balance.leave_type_code === detailDialog.request.leave_type_code && (
                        <div key={balance.leave_type_code} className="flex justify-between">
                          <span>{balance.name}</span>
                          <span className="font-medium">
                            {balance.remaining_days} / {balance.entitled_days} {t('leave.days')}
                          </span>
                        </div>
                      )
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialog({ open: false, request: null })}>
              {t('common.cancel')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Approve Dialog */}
      <AlertDialog open={approveDialog.open} onOpenChange={(open) => setApproveDialog({ open, request: approveDialog.request })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.approveRequest')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.approveLeaveConfirmation')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {approveDialog.request && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 rounded-lg space-y-2">
                <p><strong>{t('admin.requester')}:</strong> {approveDialog.request.user_name}</p>
                <p><strong>{t('admin.leaveType')}:</strong> {approveDialog.request.leave_type_name}</p>
                <p><strong>{t('leave.selectedDates') }:</strong> {
                  approveDialog.request.selected_days && approveDialog.request.selected_days.length > 0
                    ? approveDialog.request.selected_days.map(d => format(new Date(d), 'd MMM', { locale: dateLocale })).join(', ')
                    : `${approveDialog.request.start_date} - ${approveDialog.request.end_date}`
                }</p>
                <p><strong>{t('admin.daysCount')}:</strong> {approveDialog.request.days_count || approveDialog.request.total_days}</p>
              </div>
              
              <div>
                <Label>{t('admin.adminNote')} ({t('validation.required').replace('Bu alan zorunludur', 'opsiyonel')})</Label>
                <Textarea
                  value={adminNote}
                  onChange={(e) => setAdminNote(e.target.value)}
                  placeholder={t('admin.enterReason')}
                  className="mt-1"
                />
              </div>
              
              <div className="space-y-2">
                <Label>{t('admin.notifyUser')}</Label>
                <div className="flex gap-4">
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="notify-email"
                      checked={notifyOptions.email}
                      onCheckedChange={(checked) => setNotifyOptions({...notifyOptions, email: checked})}
                    />
                    <label htmlFor="notify-email" className="text-sm">{t('admin.notifyByEmail')}</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="notify-sms"
                      checked={notifyOptions.sms}
                      onCheckedChange={(checked) => setNotifyOptions({...notifyOptions, sms: checked})}
                    />
                    <label htmlFor="notify-sms" className="text-sm">{t('admin.notifyBySms')}</label>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setApproveDialog({ open: false, request: null }); setAdminNote(''); }}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleApprove}
              disabled={processing}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.approve')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Reject Dialog */}
      <AlertDialog open={rejectDialog.open} onOpenChange={(open) => setRejectDialog({ open, request: rejectDialog.request })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.rejectRequest')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.confirmRejectLeave')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {rejectDialog.request && (
            <div className="py-4 space-y-4">
              <div className="p-4 bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 rounded-lg space-y-2">
                <p><strong>{t('admin.requester')}:</strong> {rejectDialog.request.user_name}</p>
                <p><strong>{t('admin.leaveType')}:</strong> {rejectDialog.request.leave_type_name}</p>
                <p><strong>{t('admin.daysCount')}:</strong> {rejectDialog.request.days_count || rejectDialog.request.total_days}</p>
              </div>
              
              <div>
                <Label>{t('admin.rejectReason')} *</Label>
                <Textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder={t('admin.enterReason')}
                  className="mt-1"
                  required
                />
              </div>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => { setRejectDialog({ open: false, request: null }); setRejectReason(''); }}>
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleReject}
              disabled={processing || rejectReason.length < 3}
              className="bg-red-600 hover:bg-red-700"
            >
              {processing && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              {t('common.reject')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onOpenChange={(open) => setEditDialog({ open, request: editDialog.request })}>
        <DialogContent className="max-w-3xl w-[95vw] max-h-[90vh] overflow-hidden flex flex-col p-0" data-testid="edit-leave-dialog">
          {/* Header - Fixed */}
          <div className="px-5 py-3 border-b bg-gradient-to-r from-blue-600 to-indigo-600 flex-shrink-0">
            <DialogTitle className="text-base font-semibold text-white flex items-center gap-2">
              <Edit className="h-4 w-4" />
              {t('admin.editRequest') }
            </DialogTitle>
            {editDialog.request && (
              <p className="text-sm text-blue-100 mt-0.5">
                {editDialog.request.user_name} • {editDialog.request.leave_type_name}
              </p>
            )}
          </div>
          
          {editDialog.request && (
            <div className="flex-1 overflow-y-auto overscroll-contain">
              <div className="p-4 sm:p-5">
                {/* Responsive grid - stack on mobile, side by side on desktop */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
                  
                  {/* Left Column - Calendar */}
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 mb-2">
                      <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                        <CalendarIcon className="h-4 w-4 text-blue-600" />
                      </div>
                      <div>
                        <Label className="text-sm font-medium text-slate-800 dark:text-slate-100">
                          {t('leave.selectDates') }
                        </Label>
                        <p className="text-xs text-slate-400">{t('leave.calendarTipShort') }</p>
                      </div>
                    </div>
                    
                    <div className="border rounded-lg bg-white dark:bg-slate-800 shadow-sm">
                      <Calendar
                        mode="single"
                        month={editCalendarMonth}
                        onMonthChange={setEditCalendarMonth}
                        locale={dateLocale}
                        disabled={(date) => isSunday(date)}
                        modifiers={{
                          selected: (date) => isEditDaySelected(date),
                        }}
                        modifiersClassNames={{
                          selected: 'bg-blue-600 text-white hover:bg-blue-700 font-semibold rounded-md',
                        }}
                        onDayClick={(date) => {
                          if (!isSunday(date)) {
                            toggleEditDay(date);
                          }
                        }}
                        className="w-full p-2"
                        classNames={{
                          months: "flex flex-col",
                          month: "space-y-1",
                          caption: "flex justify-center pt-1 relative items-center text-sm font-medium text-slate-700 dark:text-slate-200",
                          nav: "space-x-1 flex items-center",
                          nav_button: "h-7 w-7 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-600 dark:bg-slate-700 rounded-md inline-flex items-center justify-center",
                          table: "w-full border-collapse",
                          head_row: "flex justify-around",
                          head_cell: "text-slate-400 rounded-md w-8 font-normal text-xs",
                          row: "flex w-full mt-0.5 justify-around",
                          cell: "text-center text-sm p-0 relative",
                          day: "h-8 w-8 p-0 font-normal rounded-md hover:bg-slate-100 dark:hover:bg-slate-600 dark:bg-slate-700 transition-colors text-sm",
                          day_today: "bg-slate-100 dark:bg-slate-700 font-semibold",
                          day_disabled: "text-slate-300 opacity-50 cursor-not-allowed",
                        }}
                      />
                    </div>
                  </div>
                  
                  {/* Right Column - Selected Days & Notes */}
                  <div className="space-y-3">
                    {/* Selected Days Card */}
                    <div className="border rounded-lg bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                            {t('leave.selectedDays') }
                          </span>
                          <Badge className="bg-blue-600 text-white text-xs px-2 py-0.5">
                            {editForm.selected_days.length} {t('leave.day') }
                          </Badge>
                        </div>
                        {editForm.selected_days.length > 0 && (
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="text-xs text-red-500 hover:text-red-700 hover:bg-red-50 h-6 px-2"
                            onClick={() => setEditForm({...editForm, selected_days: []})}
                          >
                            <X className="h-3 w-3 mr-1" />
                            {t('common.clearAll') }
                          </Button>
                        )}
                      </div>
                      
                      <div className="min-h-[80px] max-h-[120px] overflow-y-auto">
                        {editForm.selected_days.length > 0 ? (
                          <div className="flex flex-wrap gap-1.5">
                            {editForm.selected_days.map((day, idx) => (
                              <Badge 
                                key={idx} 
                                className="bg-blue-500 text-white hover:bg-red-500 cursor-pointer transition-all px-2 py-0.5 text-xs font-normal"
                                onClick={() => toggleEditDay(parseISO(day))}
                              >
                                {format(parseISO(day), 'd MMM', { locale: dateLocale })}
                                <X className="ml-1 h-2.5 w-2.5" />
                              </Badge>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-col items-center justify-center h-[80px] text-slate-400">
                            <CalendarIcon className="h-6 w-6 mb-1 opacity-40" />
                            <p className="text-xs">{t('leave.noDaysSelected') }</p>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    {/* Description */}
                    <div>
                      <Label className="text-xs font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1 mb-1">
                        <FileText className="h-3 w-3" />
                        {t('leave.description') }
                      </Label>
                      <Textarea
                        value={editForm.description}
                        onChange={(e) => setEditForm({...editForm, description: e.target.value})}
                        className="text-sm resize-none h-16 bg-white dark:bg-slate-800"
                        placeholder={t('leave.descriptionPlaceholder') || 'İzin açıklaması...'}
                      />
                    </div>
                    
                    {/* Admin Note */}
                    <div>
                      <Label className="text-xs font-medium text-slate-600 dark:text-slate-300 flex items-center gap-1 mb-1">
                        <Info className="h-3 w-3 text-amber-500" />
                        {t('admin.adminNote') }
                        <span className="text-slate-400 font-normal">({t('common.optional') })</span>
                      </Label>
                      <Textarea
                        value={editForm.admin_note}
                        onChange={(e) => setEditForm({...editForm, admin_note: e.target.value})}
                        className="text-sm resize-none h-16 bg-white dark:bg-slate-800"
                        placeholder={t('admin.adminNotePlaceholder') || 'Admin notu...'}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          {/* Footer - Fixed */}
          <div className="px-5 py-3 border-t bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 flex items-center justify-between gap-3 flex-shrink-0">
            <div className="text-xs text-slate-400">
              {editForm.selected_days.length > 0 && (
                <span className="text-slate-600 dark:text-slate-300">
                  {format(parseISO(editForm.selected_days[0]), 'd MMM', { locale: dateLocale })}
                  {editForm.selected_days.length > 1 && ` - ${format(parseISO(editForm.selected_days[editForm.selected_days.length - 1]), 'd MMM', { locale: dateLocale })}`}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={() => setEditDialog({ open: false, request: null })}
                className="px-4"
              >
                {t('common.cancel') }
              </Button>
              <Button 
                size="sm"
                onClick={handleEdit} 
                disabled={processing || editForm.selected_days.length === 0}
                className="px-4 bg-blue-600 hover:bg-blue-700"
              >
                {processing && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
                <Check className="mr-1.5 h-3.5 w-3.5" />
                {t('common.save') }
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      {/* Delete Request Dialog - Step 1: Normal Confirmation */}
      <AlertDialog 
        open={deleteDialog.open && deleteDialog.step === 1} 
        onOpenChange={(open) => { if (!open) setDeleteDialog({ open: false, request: null, step: 1 }); }}
      >
        <AlertDialogContent data-testid="delete-step1-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-red-600">
              <Trash2 className="h-5 w-5" />
              {t('admin.deleteLeaveRequest')}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.confirmDeleteLeaveRequest')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {deleteDialog.request && (
            <div className="py-4">
              <div className="p-4 bg-red-50 rounded-lg space-y-2 text-sm">
                <p><strong>{t('admin.requester')}:</strong> {deleteDialog.request.user_name}</p>
                <p><strong>{t('admin.leaveType')}:</strong> {deleteDialog.request.leave_type_name}</p>
                <p><strong>{t('leave.selectedDates') }:</strong> {
                  deleteDialog.request.selected_days && deleteDialog.request.selected_days.length > 0
                    ? deleteDialog.request.selected_days.map(d => format(new Date(d), 'd MMM', { locale: dateLocale })).join(', ')
                    : `${deleteDialog.request.start_date} - ${deleteDialog.request.end_date}`
                }</p>
                <p><strong>{t('admin.daysCount')}:</strong> {deleteDialog.request.days_count || deleteDialog.request.total_days} {t('leave.days')}</p>
              </div>
              {isOldDateRequest(deleteDialog.request) && (
                <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-amber-700">
                    Bu izin talebi eski tarihli bir kayittir. Silme islemi icin ek onay gerekecektir.
                  </p>
                </div>
              )}
              <p className="mt-3 text-sm text-red-600 flex items-center gap-1">
                <AlertTriangle className="h-4 w-4" />
                {t('admin.deleteWarning')}
              </p>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteDialog({ open: false, request: null, step: 1 })} data-testid="delete-step1-cancel">
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleDeleteFirstStep(); }}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-step1-confirm"
            >
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Request Dialog - Step 2: Strong Warning for Old Records */}
      <AlertDialog 
        open={deleteDialog.open && deleteDialog.step === 2} 
        onOpenChange={(open) => { if (!open) setDeleteDialog({ open: false, request: null, step: 1 }); }}
      >
        <AlertDialogContent data-testid="delete-step2-dialog">
          <AlertDialogHeader>
            <div className="flex items-center justify-center w-14 h-14 mx-auto mb-3 rounded-full bg-red-100">
              <AlertTriangle className="h-7 w-7 text-red-600" />
            </div>
            <AlertDialogTitle className="text-center text-red-600 text-lg">
              Eski Tarihli Kayit - Dikkat!
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              Bu islem geri alinamaz. Devam etmek istediginizden emin misiniz?
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {deleteDialog.request && (
            <div className="py-4 space-y-3">
              <div className="p-4 bg-red-50 border-2 border-red-200 rounded-lg space-y-2 text-sm">
                <p><strong>{t('admin.requester')}:</strong> {deleteDialog.request.user_name}</p>
                <p><strong>{t('admin.leaveType')}:</strong> {deleteDialog.request.leave_type_name}</p>
                <p><strong>Durum:</strong> {' '}
                  <Badge variant="outline" className={cn("text-xs", statusConfig[deleteDialog.request.status]?.color)}>
                    {t(`leave.${statusConfig[deleteDialog.request.status]?.label}`)}
                  </Badge>
                </p>
                <p><strong>{t('leave.selectedDates') }:</strong> {
                  deleteDialog.request.selected_days && deleteDialog.request.selected_days.length > 0
                    ? deleteDialog.request.selected_days.map(d => format(new Date(d), 'd MMM yyyy', { locale: dateLocale })).join(', ')
                    : `${deleteDialog.request.start_date} - ${deleteDialog.request.end_date}`
                }</p>
              </div>

              <div className="p-4 bg-amber-50 border-2 border-amber-300 rounded-lg">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div className="space-y-2 text-sm text-amber-800">
                    <p className="font-semibold">Bu eski tarihli bir kayittir!</p>
                    <p>
                      Bu kaydi silmek asagidaki durumlari etkileyebilir:
                    </p>
                    <ul className="list-disc list-inside space-y-1 text-amber-700">
                      <li>Calisanin gunluk durum kayitlari (daily_status) degisebilir</li>
                      <li>Gecmis donem raporlari etkilenebilir</li>
                      <li>Izin bakiye hesaplamalari degisebilir</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          )}
          
          <AlertDialogFooter>
            <AlertDialogCancel 
              onClick={() => setDeleteDialog({ open: false, request: null, step: 1 })} 
              data-testid="delete-step2-cancel"
            >
              Vazgec
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteRequest}
              className="bg-red-600 hover:bg-red-700"
              data-testid="delete-step2-confirm"
            >
              <Trash2 className="h-4 w-4 mr-2" />
              Evet, Eski Kaydi Sil
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* HardSil (Hard Delete) Dialog - Super Admin only */}
      <AlertDialog
        open={hardDeleteDialog.open}
        onOpenChange={(open) => { if (!open) setHardDeleteDialog({ open: false, request: null, confirmText: '' }); }}
      >
        <AlertDialogContent data-testid="hard-delete-dialog">
          <AlertDialogHeader>
            <div className="flex items-center justify-center w-14 h-14 mx-auto mb-3 rounded-full bg-red-100 border-2 border-red-300">
              <ShieldAlert className="h-7 w-7 text-red-700" />
            </div>
            <AlertDialogTitle className="text-center text-red-700 text-lg">
              {t('admin.hardDeleteTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription className="text-center">
              {t('admin.hardDeleteWarning')}
            </AlertDialogDescription>
          </AlertDialogHeader>

          {hardDeleteDialog.request && (
            <div className="py-4 space-y-3">
              <div className="p-4 bg-red-50 border-2 border-red-300 rounded-lg space-y-2 text-sm">
                <p><strong>{t('admin.requester')}:</strong> {hardDeleteDialog.request.user_name}</p>
                <p><strong>{t('admin.leaveType')}:</strong> {hardDeleteDialog.request.leave_type_name}</p>
                <p><strong>Durum:</strong> {' '}
                  <Badge variant="outline" className={cn("text-xs", statusConfig[hardDeleteDialog.request.status]?.color)}>
                    {t(`leave.${statusConfig[hardDeleteDialog.request.status]?.label}`)}
                  </Badge>
                </p>
                <p><strong>{t('leave.selectedDates')}:</strong> {
                  hardDeleteDialog.request.selected_days && hardDeleteDialog.request.selected_days.length > 0
                    ? hardDeleteDialog.request.selected_days.map(d => format(new Date(d), 'd MMM yyyy', { locale: dateLocale })).join(', ')
                    : `${hardDeleteDialog.request.start_date} - ${hardDeleteDialog.request.end_date}`
                }</p>
                <p><strong>{t('admin.daysCount')}:</strong> {hardDeleteDialog.request.days_count || hardDeleteDialog.request.total_days} {t('leave.days')}</p>
              </div>

              <div>
                <Label htmlFor="hard-delete-confirm-input" className="text-sm font-semibold text-red-700">
                  {t('admin.hardDeleteConfirmPrompt')}
                </Label>
                <Input
                  id="hard-delete-confirm-input"
                  data-testid="hard-delete-confirm-input"
                  className="mt-2 border-red-300 focus-visible:ring-red-500"
                  value={hardDeleteDialog.confirmText}
                  onChange={(e) => setHardDeleteDialog(prev => ({ ...prev, confirmText: e.target.value }))}
                  placeholder={t('admin.hardDeleteConfirmWord')}
                  autoComplete="off"
                />
              </div>
            </div>
          )}

          <AlertDialogFooter>
            <AlertDialogCancel
              onClick={() => setHardDeleteDialog({ open: false, request: null, confirmText: '' })}
              data-testid="hard-delete-cancel"
            >
              {t('common.cancel')}
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); handleHardDeleteRequest(); }}
              className="bg-red-700 hover:bg-red-800 disabled:opacity-50"
              disabled={hardDeleteDialog.confirmText !== t('admin.hardDeleteConfirmWord')}
              data-testid="hard-delete-confirm"
            >
              <ShieldAlert className="h-4 w-4 mr-2" />
              {t('admin.hardDeleteConfirmButton')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Admin Create Leave Request Dialog */}
      <Dialog open={createDialog.open} onOpenChange={(open) => setCreateDialog({ open })}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CalendarIcon className="h-5 w-5 text-emerald-600" />
              {t('leaveRequestsAdmin.addForUser') }
            </DialogTitle>
            <DialogDescription>
              {t('leaveRequestsAdmin.addForUserDesc') }
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* User Selection */}
            <div className="space-y-2">
              <Label>{t('leaveRequestsAdmin.selectUser') } *</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder={t('common.search') }
                  value={userSearch}
                  onChange={(e) => setUserSearch(e.target.value)}
                  className="pl-9"
                />
              </div>
              <div className="max-h-[150px] overflow-y-auto border rounded-lg">
                {filteredUsersForCreate.length === 0 ? (
                  <div className="p-4 text-center text-slate-500 dark:text-slate-400 text-sm">
                    {t('common.noResults') }
                  </div>
                ) : (
                  filteredUsersForCreate.map(u => (
                    <div
                      key={u.id}
                      onClick={() => setCreateForm({ ...createForm, user_id: u.id })}
                      className={cn(
                        "flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700 dark:bg-slate-800 dark:bg-slate-900 border-b last:border-b-0",
                        createForm.user_id === u.id && "bg-emerald-50"
                      )}
                    >
                      <div className="w-8 h-8 rounded-full bg-slate-200 flex items-center justify-center text-sm font-medium">
                        {u.full_name?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium truncate">{u.full_name}</p>
                        <p className="text-xs text-slate-500 dark:text-slate-400 truncate">{u.email}</p>
                      </div>
                      {createForm.user_id === u.id && (
                        <CheckCircle className="h-5 w-5 text-emerald-600" />
                      )}
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Leave Type Selection */}
            <div className="space-y-2">
              <Label>{t('leave.type') } *</Label>
              <Select
                value={createForm.leave_type_code}
                onValueChange={(v) => setCreateForm({ ...createForm, leave_type_code: v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder={t('leave.selectType') } />
                </SelectTrigger>
                <SelectContent>
                  {allLeaveTypes.map(lt => (
                    <SelectItem key={lt.code} value={lt.code}>
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: lt.color || '#3B82F6' }}
                        />
                        {lt.name_i18n?.[language] || lt.name_i18n?.tr || lt.code}
                        {lt.admin_only && (
                          <Badge variant="outline" className="ml-2 text-[10px] bg-purple-50 text-purple-700">
                            Admin
                          </Badge>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Date Selection - Single Calendar with Multi-Select */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
                    <CalendarIcon className="h-4 w-4 text-blue-600" />
                  </div>
                  <div>
                    <Label className="text-sm font-medium text-slate-800 dark:text-slate-100">
                      {t('leave.selectDates') } *
                    </Label>
                    <p className="text-xs text-slate-400">{t('leave.calendarTipShort') || 'Günlere tıklayarak seçin (Pazar hariç)'}</p>
                  </div>
                </div>
                {/* Show max days info if leave type selected */}
                {createForm.leave_type_code && getSelectedLeaveTypeInfo()?.default_limit_days && (
                  <Badge variant="outline" className="bg-amber-50 border-amber-200 text-amber-700">
                    {t('leave.maxDays') }: {getSelectedLeaveTypeInfo().default_limit_days} {t('leave.day') }
                  </Badge>
                )}
              </div>
              
              {/* Warning and disabled state if no leave type selected */}
              {!createForm.leave_type_code ? (
                <div className="bg-slate-100 dark:bg-slate-700 border border-slate-200 dark:border-slate-600 rounded-lg p-6 text-center">
                  <CalendarIcon className="h-10 w-10 mx-auto mb-3 text-slate-400" />
                  <p className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-1">
                    {t('leave.selectLeaveTypeFirst') }
                  </p>
                  <p className="text-xs text-slate-400">
                    {t('leave.selectLeaveTypeFirstDesc') }
                  </p>
                </div>
              ) : (
              <>
              <div className="border rounded-lg bg-white dark:bg-slate-800 shadow-sm">
                <Calendar
                  mode="single"
                  month={createCalendarMonth}
                  onMonthChange={setCreateCalendarMonth}
                  locale={dateLocale}
                  disabled={(date) => isSunday(date)}
                  modifiers={{
                    selected: (date) => isCreateDaySelected(date),
                  }}
                  modifiersClassNames={{
                    selected: 'bg-emerald-600 text-white hover:bg-emerald-700 font-semibold rounded-md',
                  }}
                  onDayClick={(date) => {
                    if (!isSunday(date)) {
                      toggleCreateDay(date);
                    }
                  }}
                  className="w-full p-2"
                  classNames={{
                    months: "flex flex-col",
                    month: "space-y-1",
                    caption: "flex justify-center pt-1 relative items-center text-sm font-medium text-slate-700 dark:text-slate-200",
                    nav: "space-x-1 flex items-center",
                    nav_button: "h-7 w-7 bg-transparent hover:bg-slate-100 dark:hover:bg-slate-600 dark:bg-slate-700 rounded-md inline-flex items-center justify-center",
                    table: "w-full border-collapse",
                    head_row: "flex justify-around",
                    head_cell: "text-slate-400 rounded-md w-8 font-normal text-xs",
                    row: "flex w-full mt-0.5 justify-around",
                    cell: "text-center text-sm p-0 relative",
                    day: "h-8 w-8 p-0 font-normal rounded-md hover:bg-slate-100 dark:hover:bg-slate-600 dark:bg-slate-700 transition-colors text-sm",
                    day_today: "bg-slate-100 dark:bg-slate-700 font-semibold",
                    day_disabled: "text-slate-300 opacity-50 cursor-not-allowed",
                  }}
                />
              </div>
              
              {/* Selected Days Summary */}
              <div className="border rounded-lg bg-slate-50 dark:bg-slate-800 dark:bg-slate-900 p-3">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
                      {t('leave.selectedDays') }
                    </span>
                    <Badge className="bg-emerald-600 text-white text-xs px-2 py-0.5">
                      {createForm.selected_days.length} {t('leave.day') }
                    </Badge>
                  </div>
                  {createForm.selected_days.length > 0 && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2 text-xs text-slate-500 dark:text-slate-400 hover:text-red-600"
                      onClick={() => setCreateForm({ ...createForm, selected_days: [], start_date: null, end_date: null })}
                    >
                      {t('common.clearAll') }
                    </Button>
                  )}
                </div>
                
                {createForm.selected_days.length === 0 ? (
                  <p className="text-xs text-slate-400 italic">{t('leave.noDaysSelected') }</p>
                ) : (
                  <div className="flex flex-wrap gap-1">
                    {createForm.selected_days.map((day, idx) => (
                      <Badge key={idx} variant="outline" className="text-xs bg-white dark:bg-slate-800">
                        {format(parseISO(day), 'd MMM', { locale: dateLocale })}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
              </>
              )}
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label>{t('leave.description') }</Label>
              <Textarea
                value={createForm.description}
                onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })}
                placeholder={t('leave.descriptionPlaceholder') || 'İzin açıklaması...'}
                rows={2}
              />
            </div>

            {/* Auto Approve Toggle */}
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg border border-emerald-200">
              <div>
                <Label className="text-emerald-900">{t('leaveRequestsAdmin.autoApprove') }</Label>
                <p className="text-xs text-emerald-600 mt-1">{t('leaveRequestsAdmin.autoApproveDesc') }</p>
              </div>
              <Switch
                checked={createForm.auto_approve}
                onCheckedChange={(v) => setCreateForm({ ...createForm, auto_approve: v })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDialog({ open: false })}>
              {t('common.cancel')}
            </Button>
            <Button
              onClick={handleCreateLeaveRequest}
              disabled={processing || !createForm.user_id || !createForm.leave_type_code || createForm.selected_days.length === 0}
              className="bg-emerald-600 hover:bg-emerald-700"
            >
              {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarIcon className="h-4 w-4 mr-2" />}
              {createForm.auto_approve 
                ? (t('leaveRequestsAdmin.createAndApprove') )
                : (t('leaveRequestsAdmin.create') )
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Şub 2026 — Otomatik iptal edilen izin raporu */}
      <Dialog open={autoCancelReportOpen} onOpenChange={setAutoCancelReportOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="auto-cancel-report-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileBarChart className="h-5 w-5 text-indigo-600" />
              Otomatik İptal Raporu
            </DialogTitle>
            <DialogDescription>
              Çalışma Durumu sayfasından &quot;İzinli&quot; durumu değiştirildiği için otomatik iptal edilen / kısmen düşülen izinlerin haftalık özeti.
            </DialogDescription>
          </DialogHeader>

          {/* Tarih filtresi */}
          <div className="flex flex-col sm:flex-row items-end gap-2 pb-3 border-b">
            <div className="flex-1 w-full">
              <Label className="text-xs text-slate-500">Başlangıç</Label>
              <Input
                type="date"
                value={autoCancelRange.from}
                onChange={(e) => setAutoCancelRange({ ...autoCancelRange, from: e.target.value })}
                data-testid="auto-cancel-report-from"
              />
            </div>
            <div className="flex-1 w-full">
              <Label className="text-xs text-slate-500">Bitiş</Label>
              <Input
                type="date"
                value={autoCancelRange.to}
                onChange={(e) => setAutoCancelRange({ ...autoCancelRange, to: e.target.value })}
                data-testid="auto-cancel-report-to"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchAutoCancelReport}
              disabled={autoCancelReport.loading}
              data-testid="auto-cancel-report-refresh"
            >
              <RefreshCw className={cn("h-4 w-4 mr-1", autoCancelReport.loading && "animate-spin")} />
              Yenile
            </Button>
          </div>

          {autoCancelReport.loading ? (
            <div className="flex items-center justify-center py-8 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Yükleniyor...
            </div>
          ) : autoCancelReport.data ? (
            <>
              {/* Totals */}
              <div className="grid grid-cols-3 gap-2 mt-3">
                <div className="rounded-lg border p-3 bg-slate-50 dark:bg-slate-900">
                  <div className="text-xs text-slate-500">Toplam</div>
                  <div className="text-2xl font-bold" data-testid="auto-cancel-total">{autoCancelReport.data.totals?.total || 0}</div>
                </div>
                <div className="rounded-lg border p-3 bg-rose-50 border-rose-200">
                  <div className="text-xs text-rose-600">Tam İptal</div>
                  <div className="text-2xl font-bold text-rose-700" data-testid="auto-cancel-full">{autoCancelReport.data.totals?.full_cancel || 0}</div>
                </div>
                <div className="rounded-lg border p-3 bg-amber-50 border-amber-200">
                  <div className="text-xs text-amber-600">Kısmi Gün Düşüldü</div>
                  <div className="text-2xl font-bold text-amber-700" data-testid="auto-cancel-partial">{autoCancelReport.data.totals?.partial_day_removed || 0}</div>
                </div>
              </div>

              {/* Weekly summary */}
              <div className="mt-4">
                <h4 className="text-sm font-semibold mb-2">Haftalık Özet</h4>
                {(autoCancelReport.data.weekly || []).length === 0 ? (
                  <p className="text-xs text-slate-400 py-3 text-center">Bu aralıkta otomatik iptal yok.</p>
                ) : (
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-slate-100 dark:bg-slate-800">
                        <tr>
                          <th className="text-left px-3 py-2">Hafta</th>
                          <th className="text-right px-3 py-2">Tam İptal</th>
                          <th className="text-right px-3 py-2">Kısmi</th>
                          <th className="text-right px-3 py-2">Toplam</th>
                        </tr>
                      </thead>
                      <tbody>
                        {autoCancelReport.data.weekly.map((w) => (
                          <tr key={w.week} className="border-t" data-testid={`auto-cancel-week-${w.week}`}>
                            <td className="px-3 py-2 font-mono">{w.week}</td>
                            <td className="text-right px-3 py-2 text-rose-600">{w.full_cancel}</td>
                            <td className="text-right px-3 py-2 text-amber-600">{w.partial_day_removed}</td>
                            <td className="text-right px-3 py-2 font-semibold">{w.total}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Events list */}
              <div className="mt-4">
                <h4 className="text-sm font-semibold mb-2">Olay Detayı (son {(autoCancelReport.data.events || []).length})</h4>
                {(autoCancelReport.data.events || []).length === 0 ? (
                  <p className="text-xs text-slate-400 py-3 text-center">Kayıt yok.</p>
                ) : (
                  <div className="space-y-2 max-h-80 overflow-y-auto pr-1">
                    {autoCancelReport.data.events.map((e) => (
                      <div key={e.id} className="rounded-md border px-3 py-2 text-sm bg-white dark:bg-slate-900" data-testid={`auto-cancel-event-${e.id}`}>
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <div className="flex items-center gap-2">
                            {e.action_kind === 'FULL_CANCEL' ? (
                              <Badge className="bg-rose-600 text-white text-[10px]">Tam İptal</Badge>
                            ) : (
                              <Badge className="bg-amber-600 text-white text-[10px]">Kısmi</Badge>
                            )}
                            <span className="font-medium">{e.user_name || '—'}</span>
                            <span className="text-xs text-slate-500 font-mono">{e.date}</span>
                          </div>
                          <span className="text-[11px] text-slate-500 font-mono">{e.created_at ? new Date(e.created_at).toLocaleString('tr-TR') : ''}</span>
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Yeni durum: <span className="font-mono">{e.new_daily_status}</span> · Değiştiren: <span className="font-medium">{e.admin_name || '—'}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          ) : (
            <p className="text-sm text-slate-400 py-6 text-center">Veri yok.</p>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoCancelReportOpen(false)}>Kapat</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminLeaveRequests;
